import { useCurrentAccount, useTipawStore } from '@application/api';
import { JOIN_PRO_ROUTES, PRO_ROUTES } from '@application/constants';
import type { Route } from '@application/types';
import { useLanguageSelectorButtons, useRouter, useShowArticles } from '@application/utils';
import type { HeaderProps } from '@presentation/components';
import { Header } from '@presentation/components';
import { useCallback, useMemo } from 'react';
import { UnfinishedApplicationController } from '../UnfinishedApplicationController';
import { adoptionsLinks, joinProLinks, proLinks } from './links';

export const HEADER_NO_BUTTONS_ROUTES: Route[] = ['booking', 'agendaConfig'];
export const HEADER_NO_LINKS_ROUTES: Route[] = ['booking', 'agendaConfig'];

export const HeaderController = () => {
  const { routeLocationDescriptor, navigateToRoute, infoFromCurrentPath, translate } = useRouter();
  const { currentAccountLoggedIn } = useCurrentAccount();
  const { languageSelectorButtons } = useLanguageSelectorButtons();
  const { shouldShowArticles } = useShowArticles();
  const { onStickyBannerClick, onStoreHeaderNavbarClick, showStickyBanner } = useTipawStore();

  const showButtons = !HEADER_NO_BUTTONS_ROUTES.includes(infoFromCurrentPath.route);
  const showLinks = !HEADER_NO_LINKS_ROUTES.includes(infoFromCurrentPath.route);

  const homeClickHandler: HeaderProps['onHomeClick'] = useCallback(() => {
    navigateToRoute('homeLanding');
  }, [navigateToRoute]);

  const links: HeaderProps['links'] = useMemo(() => {
    if (!showLinks) return;
    const returnLinks: HeaderProps['links'] = [];
    if (PRO_ROUTES.includes(infoFromCurrentPath.route)) {
      returnLinks.push(
        ...proLinks(translate, navigateToRoute, routeLocationDescriptor, 'proLanding'),
      );
    } else if (JOIN_PRO_ROUTES.includes(infoFromCurrentPath.route)) {
      returnLinks.push(...joinProLinks(translate, navigateToRoute, routeLocationDescriptor));
    } else {
      returnLinks.push(
        ...adoptionsLinks(
          translate,
          navigateToRoute,
          routeLocationDescriptor,
          currentAccountLoggedIn,
          shouldShowArticles,
        ),
      );
    }
    return returnLinks;
  }, [
    shouldShowArticles,
    currentAccountLoggedIn,
    infoFromCurrentPath.route,
    navigateToRoute,
    routeLocationDescriptor,
    showLinks,
    translate,
  ]);

  const buttons = useMemo((): HeaderProps['buttons'] => {
    if (!showButtons) return;
    if (JOIN_PRO_ROUTES.includes(infoFromCurrentPath.route)) {
      return [
        {
          label: translate('common.buttons.registration'),
          link: routeLocationDescriptor('joinLanding'),
          style: 'main',
        },
      ];
    }
    if (currentAccountLoggedIn) {
      return [
        {
          label: translate('glossary.myTipaw'),
          link: routeLocationDescriptor('mtApplications'),
          style: 'main',
        },
      ];
    }
    return [
      {
        label: translate('common.buttons.loggingin'),
        link: routeLocationDescriptor('login'),
      },
      {
        label: translate('common.buttons.registration'),
        link: routeLocationDescriptor('joinLanding'),
        style: 'main',
      },
    ];
  }, [
    showButtons,
    infoFromCurrentPath.route,
    currentAccountLoggedIn,
    translate,
    routeLocationDescriptor,
  ]);

  const showStoreLink = useMemo(() => {
    return !!links?.length && !PRO_ROUTES.includes(infoFromCurrentPath.route);
  }, [infoFromCurrentPath.route, links?.length]);

  return (
    <Header
      links={links}
      onHomeClick={homeClickHandler}
      buttons={buttons}
      canUseMobileNav={showButtons || showLinks}
      unfinishedApplicationComponent={<UnfinishedApplicationController />}
      languageSelectorButtons={languageSelectorButtons}
      onStickyBlueBannerClick={onStickyBannerClick}
      onStoreNavbarClick={onStoreHeaderNavbarClick}
      showStoreLink={showStoreLink}
      showStickyBlueBanner={showStickyBanner}
    />
  );
};
